import React, { useCallback, useState, forwardRef } from "react";
import PropTypes from "prop-types";
import find from "lodash/find";

import { MetaType } from "core/enums";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  Zoom,
  Typography
} from "@mui/material";

import { MetaKeySelect } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaKeySelect";
import { MetaDataEditField } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaDataEditField";

import { styled } from "@mui/material/styles";

// eslint-disable-next-line react/display-name
const Transition = forwardRef((props, ref) => <Zoom ref={ref} {...props} />);

export function MetaDataAdditionDialog({
  open,
  onClose,
  caseModel,
  onAttributeChanged
}) {
  const [attributeValueError, setAttributeValueError] = useState("");
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [attributeFieldValue, setAttributeFieldValue] = useState("");
  const [attributeTypeError, setAttributeTypeError] = useState("");
  const [categoricalValues, setCategoricalValues] = useState([]);

  const handleAttributeTypeSelected = attribute => {
    setSelectedAttribute(attribute);
    setAttributeFieldValue("");
    setAttributeTypeError("");

    if (doesAttributeExistInCase(attribute)) {
      setAttributeTypeError(
        `The attribute ${attribute.name} already exists on this case`
      );
    }

    if (attribute?.data_type?.id === MetaType.CATEGORY) {
      setCategoricalValues(attribute?.data_value_details);
    }
  };

  const doesAttributeExistInCase = useCallback(
    attribute => {
      return Boolean(
        find(caseModel?.attributes, ({ type_id }) => type_id === attribute.id)
      );
    },
    [caseModel?.attributes]
  );

  const handleAttributeAdded = useCallback(() => {
    if (!doesAttributeExistInCase(selectedAttribute)) {
      const { id, name, data_type, data_value_details } = selectedAttribute;
      const formattedName = name
        .replaceAll(" ", "_")
        .replace("(", "")
        .replace(")", "")
        .toLowerCase();

      let parsedAttributeValue = attributeFieldValue;
      if (selectedAttribute?.data_type?.id === MetaType.NUMBER)
        parsedAttributeValue = parseFloat(attributeFieldValue);

      onAttributeChanged(
        {
          name: formattedName,
          type_id: id,
          type_name: name,
          value: parsedAttributeValue,
          data_value_details
        },
        data_type
      );

      onClose();
    }
  }, [
    attributeFieldValue,
    doesAttributeExistInCase,
    onAttributeChanged,
    onClose,
    selectedAttribute
  ]);

  const isAddAttributeDisabled =
    Boolean(attributeTypeError) ||
    Boolean(attributeValueError) ||
    !attributeFieldValue ||
    !selectedAttribute;

  return (
    <Dialog
      keepMounted
      open={open}
      maxWidth="sm"
      fullWidth={true}
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <DialogHeader>Add attributes to case</DialogHeader>
        <DialogSubTitle>
          To add a new attribute to the case, select an attribute name from the
          dropdown, then specify a value.
        </DialogSubTitle>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container>
          <GridWithMarginTop item xs={12}>
            <MetaKeySelect
              attribute={selectedAttribute}
              attributeTypeError={attributeTypeError}
              onAttributeTypeSelected={handleAttributeTypeSelected}
            />
          </GridWithMarginTop>
          <GridWithMarginTop item xs={12}>
            {selectedAttribute && (
              <MetaDataEditField
                attributeDataType={selectedAttribute?.data_type}
                attributeFieldValue={attributeFieldValue}
                setAttributeFieldValue={setAttributeFieldValue}
                categoricalValues={categoricalValues}
                setValidationError={setAttributeValueError}
                enableLabel
              />
            )}
          </GridWithMarginTop>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          data-qa="CM-Button-Cancel-Attribute-Modal"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAttributeAdded}
          color="primary"
          variant="contained"
          disabled={isAddAttributeDisabled}
          data-qa="CM-Button-Add-Attribute-Modal"
        >
          Add attribute
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MetaDataAdditionDialog.propTypes = {
  open: PropTypes.bool,
  caseModel: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAttributeChanged: PropTypes.func.isRequired
};

MetaDataAdditionDialog.defaultProp = {
  open: false
};

const GridWithMarginTop = styled(Grid)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const DialogHeader = styled(Typography)`
  text-transform: none;
  font-weight: bold;
`;

const DialogSubTitle = styled("span")`
  font-size: 12px;
`;
